import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Coins, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { CreditPlansGrid } from '@/components/billing/CreditPlansGrid';
import { useCreditRecharge } from '@/lib/billing/useCreditRecharge';
import { useCreditPlans } from '@/lib/billing/useCreditPlans';
import { parseInsufficientCreditsDetail, isInsufficientCreditsError } from '@/lib/billing/creditPlans';
import { formatApiError } from '@/lib/formatApiError';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const InsufficientCreditsContext = createContext(null);

export function useInsufficientCredits() {
  const ctx = useContext(InsufficientCreditsContext);
  if (!ctx) {
    throw new Error('useInsufficientCredits must be used within InsufficientCreditsProvider');
  }
  return ctx;
}

export function handleInsufficientCreditsError(err, openInsufficientCredits) {
  if (isInsufficientCreditsError(err)) {
    openInsufficientCredits(err);
    return true;
  }
  return false;
}

export function InsufficientCreditsDialog({
  open,
  onClose,
  message = '',
  neededCredits = null,
  balance = null,
}) {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const creditsPerInr = wallet?.credits_per_inr ?? 2;
  const billingEnabled = wallet?.billing_enabled !== false;

  const refreshWallet = useCallback(async () => {
    try {
      const { data } = await api.get('/billing/wallet');
      setWallet(data);
    } catch {
      setWallet(null);
    }
  }, []);

  useEffect(() => {
    if (open) refreshWallet();
  }, [open, refreshWallet]);

  const { startRecharge, payingPlanId } = useCreditRecharge({
    onSuccess: () => {
      refreshWallet();
      onClose?.();
    },
  });

  const { plans, loading: plansLoading } = useCreditPlans({ enabled: open });

  const handleBuyPlan = (plan) => {
    if (plan.custom) return;
    onClose?.();
    startRecharge(plan);
  };

  const shortfall =
    neededCredits != null && balance != null
      ? Math.max(0, neededCredits - balance)
      : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="!fixed !top-1/2 !left-1/2 !-translate-x-1/2 !-translate-y-1/2 w-[min(96vw,1240px)] max-w-6xl max-h-[88vh] overflow-hidden rounded-[24px] p-0 gap-0 border border-white/15 bg-[#080b11]/98 backdrop-blur-2xl shadow-[0_0_80px_rgba(0,0,0,0.95)] text-white font-jakarta flex flex-col z-[120]"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '88vh',
          isolation: 'isolate',
        }}
        data-testid="insufficient-credits-dialog"
      >
        {/* Ambient Corner Glows */}
        <div
          className="absolute -top-24 -left-24 w-[380px] pointer-events-none z-[1] select-none opacity-40"
          dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-[380px] pointer-events-none z-[1] select-none opacity-40"
          dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
        />

        {/* Dialog Header */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-white/[0.08] relative z-10 bg-white/[0.01] shrink-0">
          <DialogHeader className="text-left space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shadow-inner">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg sm:text-xl font-black font-sora text-white tracking-tight leading-tight">
                  You need more credits
                </DialogTitle>
                <DialogDescription className="text-xs text-[#9CA3AF] mt-0.5 leading-snug font-normal font-jakarta">
                  {message || 'Your wallet balance is too low for this generation task.'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Balance Breakdown Stat Pills */}
          <div className="mt-3 flex flex-wrap gap-2">
            {balance != null ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-mono font-bold bg-white/[0.04] border border-white/10 text-gray-300 shadow-sm">
                <Coins className="w-3.5 h-3.5 text-[#2DD4BF]" />
                Balance: {balance.toFixed(2)} credits
              </span>
            ) : null}
            {neededCredits != null ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-mono font-bold bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 text-[#62FAE3] shadow-sm">
                Needed: ~{neededCredits.toFixed(2)} credits
              </span>
            ) : null}
            {shortfall != null && shortfall > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-mono font-bold bg-rose-500/15 border border-rose-500/30 text-rose-300 shadow-sm">
                Short by ~{shortfall.toFixed(2)} credits
              </span>
            ) : null}
          </div>
        </div>

        {/* Dialog Body - Plans Grid */}
        <div className="px-5 py-4 sm:px-6 sm:py-4 space-y-3 relative z-10 flex-1 overflow-y-auto min-h-0 pr-2 scrollbar-none">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xs sm:text-sm font-bold font-sora text-white flex items-center gap-1.5">
                <span>Choose a plan</span>
                <TwinneeLogo className="w-3 h-3 text-[#2DD4BF]" />
              </h3>
              <p className="text-[11px] text-[#9CA3AF]">
                Credits never expire. Pay only when generation succeeds — failed jobs are not charged.
              </p>
            </div>
          </div>

          <CreditPlansGrid
            singleRow
            compact
            plans={plans}
            creditsPerInr={creditsPerInr}
            billingEnabled={billingEnabled}
            onBuyPlan={handleBuyPlan}
            payingPlanId={payingPlanId}
            loading={plansLoading}
          />
        </div>

        {/* Dialog Footer */}
        <div className="px-5 py-3 sm:px-6 sm:py-3.5 border-t border-white/[0.08] bg-black/50 shrink-0 relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 font-jakarta">
          <button
            type="button"
            className="text-xs font-bold text-[#62FAE3] hover:text-white transition-colors flex items-center gap-1.5 cursor-pointer self-center sm:self-auto"
            onClick={() => {
              onClose?.();
              navigate('/pricing');
            }}
          >
            <span>View full pricing & custom recharge</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            className="px-4 py-1.5 rounded-xl text-xs font-bold text-gray-300 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] border border-white/15 transition-all cursor-pointer shadow-sm"
            onClick={onClose}
          >
            Continue editing
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InsufficientCreditsProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    message: '',
    neededCredits: null,
    balance: null,
  });

  const openInsufficientCredits = useCallback((errOrMessage) => {
    const message =
      typeof errOrMessage === 'string'
        ? errOrMessage
        : formatApiError(errOrMessage, 'Insufficient credits');
    const { neededCredits, balance } = parseInsufficientCreditsDetail(message);
    setState({ open: true, message, neededCredits, balance });
  }, []);

  const closeInsufficientCredits = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <InsufficientCreditsContext.Provider
      value={{ openInsufficientCredits, closeInsufficientCredits }}
    >
      {children}
      <InsufficientCreditsDialog
        open={state.open}
        onClose={closeInsufficientCredits}
        message={state.message}
        neededCredits={state.neededCredits}
        balance={state.balance}
      />
    </InsufficientCreditsContext.Provider>
  );
}

export default InsufficientCreditsProvider;
